import {
  StockItem,
  StockItemUpdateFieldValue,
  Supplier,
} from '../../../../shared-utilities/models-old/datastructures';
import {ISupplierOption, IVatRateOption} from '../../models/add-stock-item-interfaces';
import {FormGroup} from '@angular/forms';
import {ISearchableFields} from '../../../../shared/shared-models/type-sense/default-searchable-fields';

/**
 * Field 36 (vatR) must be numeric for the backend. VAT option ids are strings in the UI lookup map.
 */
export function parseVatRateId(id: string | undefined | null): StockItemUpdateFieldValue {
  if (id === null || id === undefined || id === '') {
    return '';
  }
  const parsed: number = Number(id);
  return Number.isNaN(parsed) ? '' : parsed;
}

export function createStockItemFromForm(form: FormGroup): StockItem {
  const vatRateObj = form.get('pricing.vatRate')?.value as IVatRateOption | null;

  return {
    isNewStockItem: true,
    dep: form.get('basic.dep')?.value || '',
    latestCost: (form.get('pricing.latestCost')?.value as number) || 0,
    nominalGP: (form.get('pricing.nominalGP')?.value as number) || 0,
    sellPriExcl1: (form.get('pricing.sellPriExcl1')?.value as number) || 0,
    sellPriIncl1: (form.get('pricing.sellPriIncl1')?.value as number) || 0,
    subDep: form.get('basic.subDep')?.value || '',
    vatR: (parseVatRateId(vatRateObj?.id) || 0) as number,
  } as unknown as StockItem;
}

export function isSupplierMap(value: unknown): value is Record<string, Supplier> {
  if (!value || typeof value !== 'object') return false;
  const first = Object.values(value as Record<string, unknown>)[0] as Supplier | undefined;
  return !!first && typeof first.name === 'string';
}

export function objectToSupplierOptions(obj: Record<string, Supplier> | ISearchableFields): ISupplierOption[] {
  if (!obj) return [];
  if (isSupplierMap(obj)) {
    return Object.keys(obj).map((key: string) => ({
      key,
      description: obj[key].name,
    }));
  }
  return [];
}

//#region Price Calculation Methods

export function calculateExclusivePrice(priceIncl: number, vatRate: number): number {
  if (priceIncl === 0 || vatRate === 0) return priceIncl;
  const result = priceIncl / (1 + vatRate / 100);
  return Math.round(result * 100) / 100;
}

export function calculateInclusivePrice(priceExcl: number, vatRate: number): number {
  if (priceExcl === 0 || vatRate === 0) return priceExcl;
  const result = priceExcl * (1 + vatRate / 100);
  return Math.round(result * 100) / 100;
}

export function calculateNominalGP(sellPriceIncl: number, latestCost: number, vatRate: number): number {
  if (!sellPriceIncl || !latestCost || latestCost === 0) return 0;
  const sellPriceExcl: number = sellPriceIncl / (1 + (vatRate / 100));
  const gp: number = ((sellPriceExcl - latestCost) / sellPriceExcl) * 100;
  return Math.round(gp * 100) / 100;
}

export function updatePricesFromInclusive(value: number, form: FormGroup): void {
  const vatRateObj = form.get('pricing.vatRate')?.value as IVatRateOption | null;
  const vatRate: number = vatRateObj?.rate || 0;
  if (vatRate === 0) {
    form.get('pricing.sellPriExcl1')?.setValue(value, {emitEvent: false});
    return;
  }
  const exclPrice = calculateExclusivePrice(value, vatRate);
  form.get('pricing.sellPriExcl1')?.setValue(exclPrice, {emitEvent: false});

  const latestCost = (form.get('pricing.latestCost')?.value as number) || 0;
  if (latestCost > 0) {
    const nominalGP = calculateNominalGP(value, latestCost, vatRate);
    form.get('pricing.nominalGP')?.setValue(nominalGP, {emitEvent: false});
  }
}

export function updatePricesFromExclusive(value: number, form: FormGroup): void {
  const vatRateObj = form.get('pricing.vatRate')?.value as IVatRateOption | null;
  const vatRate: number = vatRateObj?.rate || 0;
  if (vatRate === 0) {
    form.get('pricing.sellPriIncl1')?.setValue(value, {emitEvent: false});
    return;
  }
  const inclPrice = calculateInclusivePrice(value, vatRate);
  form.get('pricing.sellPriIncl1')?.setValue(inclPrice, {emitEvent: false});

  const latestCost = (form.get('pricing.latestCost')?.value as number) || 0;
  if (latestCost > 0) {
    const nominalGP = calculateNominalGP(inclPrice, latestCost, vatRate);
    form.get('pricing.nominalGP')?.setValue(nominalGP, {emitEvent: false});
  }
}

//#endregion
